import { useEffect, useRef, type ReactNode } from 'react'

/**
 * Modal accesible reutilizable: overlay oscurecido + caja centrada.
 *
 * - Cierra con Escape, clic en el fondo, o el botón ✕ (si se usa la cabecera).
 * - Atrapa el foco dentro mientras está abierto y lo devuelve al cerrar.
 * - Bloquea el scroll del body: el contenido scrollea DENTRO del modal, no la
 *   página (que es justo lo que evita la mala experiencia del panel lateral).
 *
 * Es presentacional: el contenido y el ancho los decide quien lo usa.
 */

interface Props {
  /** Etiqueta accesible del diálogo. */
  titulo: string
  /** Cerrar el modal (Escape / clic fondo / ✕). */
  onCerrar: () => void
  /** Ancho máximo (clase Tailwind), p. ej. 'max-w-2xl'. */
  anchoMax?: string
  children: ReactNode
}

export function Modal({ titulo, onCerrar, anchoMax = 'max-w-xl', children }: Props) {
  const cajaRef = useRef<HTMLDivElement>(null)

  // Escape cierra; bloquea el scroll del body mientras está abierto.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar()
    }
    document.addEventListener('keydown', onKey)
    // Enfoca la caja al abrir (para que Escape y el foco atrapado funcionen).
    cajaRef.current?.focus()
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [onCerrar])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // Clic en el fondo (no en la caja) cierra.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCerrar()
      }}
    >
      {/* Fondo oscurecido */}
      <div aria-hidden className="absolute inset-0 bg-[rgba(3,9,17,0.68)] backdrop-blur-[2px]" />

      {/* Caja */}
      <div
        ref={cajaRef}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        className={[
          'relative flex max-h-[85vh] w-full flex-col overflow-hidden rounded-2xl border border-borde bg-superficie shadow-2xl outline-none',
          anchoMax,
        ].join(' ')}
      >
        {children}
      </div>
    </div>
  )
}
